import React, { useEffect, useState, useMemo } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Loader2, Lock, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { isDetailedStatsUnlocked } from "@/lib/detailedStatsAccess";
import {
  statsCarShortLabel,
  statsColorForCar,
  statsEnabledCars,
} from "@/lib/statsChartColors";

const RANGES = [
  { key: 7, label: "7 nap" },
  { key: 14, label: "14 nap" },
  { key: 30, label: "30 nap" },
];

const shortDate = (iso) => {
  const p = iso.split("-");
  return `${p[1]}.${p[2]}`;
};

function emptySeries(days) {
  const out = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (days - 1));
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    out.push({ date: iso, views: 0, interest: 0, label: shortDate(iso) });
  }
  return out;
}

export function StatsChart({ cars = [], carId: carIdProp, onCarIdChange }) {
  const [days, setDays] = useState(14);
  const [carIdInternal, setCarIdInternal] = useState("all");
  const carId = carIdProp ?? carIdInternal;
  const setCarId = (id) => {
    onCarIdChange?.(id);
    if (carIdProp === undefined) setCarIdInternal(id);
  };

  const statsCars = useMemo(() => statsEnabledCars(cars), [cars]);
  const statsCarIds = useMemo(() => statsCars.map((c) => c.id), [statsCars]);
  const activeColor = useMemo(() => {
    if (carId === "all") return "#2563EB";
    return statsColorForCar(carId, statsCarIds);
  }, [carId, statsCarIds]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [dailyPerCar, setDailyPerCar] = useState(5);
  const [guaranteeCredits, setGuaranteeCredits] = useState(60);
  const [guaranteeDays, setGuaranteeDays] = useState(14);
  const [chartMounted, setChartMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setChartMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const unlockedLocal = isDetailedStatsUnlocked(cars, carId);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { days };
    if (carId !== "all") params.car_id = carId;
    api
      .get("/my/stats/timeseries", { params })
      .then((r) => {
        if (cancelled) return;
        setLocked(!!r.data.locked);
        setDailyPerCar(Number(r.data.detailed_stats_daily_per_car) || 5);
        if (r.data.detailed_stats_guarantee_credits != null) {
          setGuaranteeCredits(Number(r.data.detailed_stats_guarantee_credits) || 60);
        }
        if (r.data.detailed_stats_guarantee_days != null) {
          setGuaranteeDays(Number(r.data.detailed_stats_guarantee_days) || 14);
        }
        setSeries(r.data.series || []);
      })
      .catch(() => {
        if (cancelled) return;
        setLocked(!unlockedLocal);
        setSeries([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days, carId, unlockedLocal]);

  const chartData = useMemo(() => {
    const base = series.length ? series : emptySeries(days);
    return base.map((s) => ({ ...s, label: s.label || shortDate(s.date) }));
  }, [series, days]);

  const showLocked = locked || !unlockedLocal;

  const totals = useMemo(() => {
    if (showLocked) return { v: "—", i: "—" };
    const v = chartData.reduce((a, b) => a + (b.views || 0), 0);
    const i = chartData.reduce((a, b) => a + (b.interest || 0), 0);
    return { v, i };
  }, [chartData, showLocked]);

  return (
    <div className="glass rounded-3xl p-5 md:p-6 mb-6 w-full min-w-0" data-testid="stats-chart">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.15em] uppercase text-slate-400">Aktivitás</div>
          <h3 className="font-heading text-xl font-light mt-0.5">Napi megtekintés és érdeklődés</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cars.length > 0 && (
            <div
              data-testid="stats-chart-car"
              className="flex flex-wrap items-center gap-1.5 max-w-full"
              role="group"
              aria-label="Hirdetés a statisztikában"
            >
              {statsCars.length > 1 && (
                <button
                  type="button"
                  data-testid="stats-chart-car-all"
                  onClick={() => setCarId("all")}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    carId === "all"
                      ? "bg-white/10 border-white/25 text-white"
                      : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-electric to-emerald-400" />
                  Összes (stat)
                </button>
              )}
              {statsCars.map((c) => {
                const color = statsColorForCar(c.id, statsCarIds);
                const active = carId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`stats-chart-car-${c.id}`}
                    onClick={() => setCarId(c.id)}
                    title={statsCarShortLabel(c)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border max-w-[200px] truncate transition-colors ${
                      active ? "text-white" : "text-slate-400 hover:text-white"
                    }`}
                    style={{
                      borderColor: active ? color : "rgba(255,255,255,0.12)",
                      backgroundColor: active ? `${color}22` : "transparent",
                      boxShadow: active ? `0 0 0 1px ${color}55` : undefined,
                    }}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="truncate">{statsCarShortLabel(c)}</span>
                  </button>
                );
              })}
              {statsCars.length === 0 && (
                <span className="text-xs text-slate-500 px-2">Nincs autó részletes statisztikával</span>
              )}
            </div>
          )}
          <div className="inline-flex rounded-full border border-white/10 p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`stats-chart-range-${r.key}`}
                onClick={() => setDays(r.key)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  days === r.key ? "bg-electric text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {statsCars.length > 0 && !showLocked && (
        <div
          data-testid="stats-chart-legend"
          className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-slate-500"
        >
          {carId === "all" && statsCars.length > 1 ? (
            statsCars.map((c) => (
              <span key={c.id} className="inline-flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: statsColorForCar(c.id, statsCarIds) }}
                />
                {statsCarShortLabel(c)}
              </span>
            ))
          ) : carId !== "all" ? (
            <span className="inline-flex items-center gap-1.5 text-slate-400">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: activeColor }} />
              Grafikon: <b className="text-slate-300 font-normal">{statsCarShortLabel(statsCars.find((c) => c.id === carId))}</b>
            </span>
          ) : null}
        </div>
      )}

      <div className="flex gap-6 mt-3 text-xs text-slate-400">
        <span>
          Megtekintés:{" "}
          <span data-testid="stats-chart-total-views" className="font-medium" style={{ color: activeColor }}>
            {totals.v}
          </span>
        </span>
        <span>
          Érdeklődés:{" "}
          <span data-testid="stats-chart-total-interest" className="text-emerald-400 font-medium">
            {totals.i}
          </span>
        </span>
      </div>

      <div className="mt-3 w-full min-w-0" data-testid="stats-chart-canvas">
        {loading ? (
          <div className="h-56 flex items-center justify-center rounded-2xl border border-white/5 bg-white/[0.02]">
            <Loader2 className="w-6 h-6 text-electric animate-spin" />
          </div>
        ) : showLocked ? (
          <div
            data-testid="stats-chart-locked"
            className="rounded-2xl border border-white/10 bg-anthracite-900/80 backdrop-blur-md px-5 py-7 md:px-8 md:py-8 shadow-xl"
          >
            <div className="max-w-lg mx-auto text-center">
              <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-5 h-5 text-slate-400" strokeWidth={1.75} />
              </div>
              <p className="font-heading text-lg text-white">Részletes statisztika — aktiválható</p>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">
                Autónként bekapcsolható a hirdetés <b className="text-slate-300">Kiegészítők</b> paneljén.
                Nincs egyszeri díj — csak napi levonás, amíg aktív.
              </p>
              <p
                data-testid="stats-chart-daily-price"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric/15 text-electric text-sm font-medium"
              >
                <BarChart3 className="w-4 h-4" strokeWidth={1.75} />
                {dailyPerCar} kredit / nap / autó
              </p>
              <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
                Görgess le a hirdetés kártyájához, és kapcsold be. {guaranteeDays} nap után gyenge
                teljesítmény esetén <b className="text-slate-400">{guaranteeCredits} kredit</b> garancia jár
                (egyszer / autó).
              </p>
            </div>
          </div>
        ) : !chartMounted ? (
          <div className="h-64 flex items-center justify-center rounded-2xl border border-white/5 bg-white/[0.02]">
            <Loader2 className="w-6 h-6 text-electric animate-spin" />
          </div>
        ) : (
          <div className="relative h-64 w-full min-w-0 overflow-visible rounded-2xl">
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200}>
                <AreaChart data={chartData} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={activeColor} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={activeColor} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gInterest" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  {!showLocked && (
                    <>
                      <Tooltip
                        contentStyle={{
                          background: "#111318",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: 12,
                          color: "#fff",
                        }}
                        labelStyle={{ color: "#94a3b8", fontSize: 12 }}
                        formatter={(val, name) => [val, name === "views" ? "Megtekintés" : "Érdeklődés"]}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: "#94a3b8" }}
                        formatter={(v) => (v === "views" ? "Megtekintés" : "Érdeklődés")}
                      />
                    </>
                  )}
                  <Area type="monotone" dataKey="views" stroke={activeColor} strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="interest" stroke="#10B981" strokeWidth={2} fill="url(#gInterest)" />
                </AreaChart>
              </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
